import React, { useEffect, useMemo, useState } from "react";
import { DatePicker, Input, Select, Table, Tabs, message } from "antd";
import { ReloadOutlined, DownloadOutlined, ClearOutlined } from "@ant-design/icons";
import axios from "axios";
import dayjs from "dayjs";
import { API_URL } from "../../constants";
import "./workingmonth.css";

const { RangePicker } = DatePicker;

const Badge = ({ tone = "neutral", children }) => (
  <span className={`wm-badge wm-badge-${tone}`}>{children}</span>
);

const OUTCOME_UI = {
  confirmed: { tone: "good", label: "Procesată" },
  not_found: { tone: "bad", label: "Venită după expirare" },
  reservation_expired: { tone: "warn", label: "Rezervare expirată" },
  ignored: { tone: "neutral", label: "Ignorată" },
  pending_status: { tone: "warn", label: "Neconfirmată de Paynet" },
  check_failed: { tone: "bad", label: "Verificare eșuată" },
  error: { tone: "bad", label: "Eroare" },
};

const fmtLei = (v) =>
  v === null || v === undefined ? "—" : `${parseFloat(v).toFixed(2)} lei`;

const RANGE_PRESETS = [
  { label: "Azi", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
  {
    label: "Ieri",
    value: [
      dayjs().subtract(1, "day").startOf("day"),
      dayjs().subtract(1, "day").endOf("day"),
    ],
  },
  { label: "Ultimele 7 zile", value: [dayjs().subtract(6, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Ultimele 30 zile", value: [dayjs().subtract(29, "day").startOf("day"), dayjs().endOf("day")] },
  { label: "Luna curentă", value: [dayjs().startOf("month"), dayjs().endOf("day")] },
  {
    label: "Luna trecută",
    value: [
      dayjs().subtract(1, "month").startOf("month"),
      dayjs().subtract(1, "month").endOf("month"),
    ],
  },
];

const PaynetRequests = () => {
  const [logs, setLogs] = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(false);
  // filtre
  const [range, setRange] = useState([
    dayjs().subtract(6, "day").startOf("day"),
    dayjs().endOf("day"),
  ]);
  const [outcomeFilter, setOutcomeFilter] = useState([]);
  const [eventFilter, setEventFilter] = useState([]);
  const [search, setSearch] = useState("");

  const fetchAll = async (r = range) => {
    setLoading(true);
    try {
      const params =
        r && r[0] && r[1]
          ? `?minDate=${encodeURIComponent(r[0].startOf("day").toISOString())}&maxDate=${encodeURIComponent(r[1].endOf("day").toISOString())}`
          : "";
      const [logsRes, pendingRes] = await Promise.all([
        axios.get(API_URL + "payment/logs" + params),
        axios.get(API_URL + "payment/pending"),
      ]);
      setLogs(logsRes.data);
      setPending(pendingRes.data);
    } catch (error) {
      console.log({ error });
      message.error("Nu am putut încărca jurnalul Paynet.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRangeChange = (r) => {
    setRange(r);
    if (r && r[0] && r[1]) fetchAll(r);
  };

  // evenimentele existente in datele incarcate (pentru filtrul Eveniment)
  const eventOptions = useMemo(() => {
    const set = new Set(logs.map((l) => l.event_type || "(fără)"));
    return Array.from(set).map((v) => ({ value: v, label: v }));
  }, [logs]);

  // pipeline-ul de filtrare (in afara tabelului, ca export-ul CSV si
  // numaratorile sa reflecte exact ce se vede)
  const filteredLogs = useMemo(() => {
    const s = search.trim().toLowerCase();
    return logs.filter((l) => {
      if (outcomeFilter.length && !outcomeFilter.includes(l.outcome)) return false;
      if (eventFilter.length && !eventFilter.includes(l.event_type || "(fără)"))
        return false;
      if (
        s &&
        ![l.external_id, l.customer, l.message, l.paynet_payment_id, l.merchant]
          .map((v) => (v || "").toLowerCase())
          .some((v) => v.includes(s))
      )
        return false;
      return true;
    });
  }, [logs, outcomeFilter, eventFilter, search]);

  const countBy = (outcome) =>
    filteredLogs.filter((l) => l.outcome === outcome).length;
  const sumaProcesata = filteredLogs
    .filter((l) => l.outcome === "confirmed")
    .reduce((s, l) => s + parseFloat(l.amount || 0), 0);

  const resetFilters = () => {
    setOutcomeFilter([]);
    setEventFilter([]);
    setSearch("");
  };

  const exportCsv = () => {
    const header = [
      "Data",
      "ExternalId",
      "Eveniment",
      "Rezultat",
      "Suma (lei)",
      "Client",
      "Merchant",
      "Paynet ID",
      "Detalii",
    ];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const rows = filteredLogs.map((l) =>
      [
        dayjs(l.createdAt).format("DD.MM.YYYY, HH:mm:ss"),
        l.external_id,
        l.event_type,
        (OUTCOME_UI[l.outcome] || { label: l.outcome }).label,
        l.amount != null ? parseFloat(l.amount).toFixed(2) : "",
        l.customer,
        l.merchant,
        l.paynet_payment_id,
        l.message,
      ]
        .map(esc)
        .join(";")
    );
    // BOM pentru diacritice corecte in Excel
    const csv = "﻿" + [header.map(esc).join(";"), ...rows].join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `paynet_${range?.[0]?.format("YYYYMMDD") || "toate"}-${range?.[1]?.format("YYYYMMDD") || ""}.csv`
    );
    document.body.appendChild(link);
    link.click();
    link.parentNode.removeChild(link);
    window.URL.revokeObjectURL(url);
    message.success(`Export: ${filteredLogs.length} înregistrări`);
  };

  const paginationProps = (total, defaultPageSize = 20) => ({
    defaultPageSize,
    pageSizeOptions: ["10", "20", "50", "100"],
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (t, r2) => `${r2[0]}–${r2[1]} din ${t} înregistrări`,
    hideOnSinglePage: false,
  });

  const logColumns = [
    {
      title: "Data",
      dataIndex: "createdAt",
      width: 150,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
      render: (v) => (
        <span className="wm-log-time">{dayjs(v).format("DD.MM.YYYY, HH:mm:ss")}</span>
      ),
    },
    {
      title: "ExternalId",
      dataIndex: "external_id",
      width: 170,
      sorter: (a, b) => (a.external_id || "").localeCompare(b.external_id || ""),
      render: (v) => <span className="wm-log-email">{v || "—"}</span>,
    },
    {
      title: "Eveniment",
      dataIndex: "event_type",
      width: 110,
      sorter: (a, b) => (a.event_type || "").localeCompare(b.event_type || ""),
      render: (v) => v || "—",
    },
    {
      title: "Rezultat",
      dataIndex: "outcome",
      width: 190,
      sorter: (a, b) => (a.outcome || "").localeCompare(b.outcome || ""),
      render: (v) => {
        const ui = OUTCOME_UI[v] || { tone: "neutral", label: v };
        return <Badge tone={ui.tone}>{ui.label}</Badge>;
      },
    },
    {
      title: "Suma",
      dataIndex: "amount",
      width: 110,
      align: "right",
      sorter: (a, b) => parseFloat(a.amount || 0) - parseFloat(b.amount || 0),
      render: (v) => <span className="wm-log-time">{fmtLei(v)}</span>,
    },
    {
      title: "Client",
      dataIndex: "customer",
      width: 150,
      sorter: (a, b) => (a.customer || "").localeCompare(b.customer || ""),
      render: (v) => v || "—",
    },
    {
      title: "Detalii",
      dataIndex: "message",
      ellipsis: true,
      render: (v) => <span style={{ fontSize: 12.5 }}>{v || "—"}</span>,
    },
  ];

  const expandedRow = (r) => {
    let payloadPretty = null;
    try {
      payloadPretty = r.payload ? JSON.stringify(JSON.parse(r.payload), null, 2) : null;
    } catch (e) {
      payloadPretty = r.payload;
    }
    return (
      <div style={{ fontSize: 12.5, display: "grid", gap: 6 }}>
        <div>
          <strong>Mesaj complet:</strong> {r.message || "—"}
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 24px" }}>
          <span><strong>Paynet ID:</strong> {r.paynet_payment_id || "—"}</span>
          <span><strong>Merchant:</strong> {r.merchant || "—"}</span>
          <span>
            <strong>Data status Paynet:</strong>{" "}
            {r.status_date ? dayjs(r.status_date).format("DD.MM.YYYY, HH:mm:ss") : "—"}
          </span>
        </div>
        {payloadPretty ? (
          <details>
            <summary style={{ cursor: "pointer", fontWeight: 600 }}>
              Payload brut de la Paynet
            </summary>
            <pre
              style={{
                background: "#f4f5f7",
                border: "1px solid #e7e8ea",
                borderRadius: 8,
                padding: 10,
                maxHeight: 300,
                overflow: "auto",
                fontSize: 11.5,
              }}
            >
              {payloadPretty}
            </pre>
          </details>
        ) : null}
      </div>
    );
  };

  const pendingColumns = [
    {
      title: "ExternalId",
      dataIndex: "external_id",
      sorter: (a, b) => (a.external_id || "").localeCompare(b.external_id || ""),
      render: (v) => <span className="wm-log-email">{v}</span>,
    },
    {
      title: "Creată la",
      dataIndex: "createdAt",
      width: 150,
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
      render: (v) => (
        <span className="wm-log-time">{dayjs(v).format("DD.MM.YYYY, HH:mm")}</span>
      ),
    },
    {
      title: "Expiră la",
      dataIndex: "createdAt",
      key: "expira",
      width: 220,
      render: (v) => {
        const exp = dayjs(v).add(2, "hour");
        const expired = exp.isBefore(dayjs());
        return (
          <span className="wm-log-time">
            {exp.format("HH:mm")}{" "}
            {expired ? (
              <Badge tone="warn">se șterge la următoarea rulare</Badge>
            ) : (
              <Badge tone="neutral">{exp.diff(dayjs(), "minute")} min rămase</Badge>
            )}
          </span>
        );
      },
    },
    {
      title: "Suma",
      width: 110,
      align: "right",
      sorter: (a, b) =>
        (a.Plati || []).reduce((s, p) => s + parseFloat(p.SUMA || 0), 0) -
        (b.Plati || []).reduce((s, p) => s + parseFloat(p.SUMA || 0), 0),
      render: (r) =>
        fmtLei((r.Plati || []).reduce((s, p) => s + parseFloat(p.SUMA || 0), 0)),
    },
    { title: "Servicii", width: 90, render: (r) => (r.Plati || []).length },
    {
      title: "Client",
      render: (r) => (r.Plati && r.Plati[0] ? r.Plati[0].USERINFO : "—"),
    },
  ];

  const historyTab = (
    <>
      <p className="wm-tab-desc">
        Toate request-urile primite de la Paynet în intervalul selectat —
        inclusiv cele sosite după expirarea rezervării, care înainte se pierdeau.
        Click pe „+” la un rând pentru toate detaliile.
      </p>

      <div className="wm-filters">
        <div className="wm-filter">
          <span className="wm-filter-label">Selectați intervalul de date</span>
          <RangePicker
            value={range}
            onChange={handleRangeChange}
            presets={RANGE_PRESETS}
            format="DD.MM.YYYY"
            allowClear={false}
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Rezultat</span>
          <Select
            mode="multiple"
            allowClear
            placeholder="Toate"
            style={{ minWidth: 200 }}
            value={outcomeFilter}
            onChange={setOutcomeFilter}
            options={Object.entries(OUTCOME_UI).map(([value, o]) => ({
              value,
              label: o.label,
            }))}
            maxTagCount="responsive"
          />
        </div>
        <div className="wm-filter">
          <span className="wm-filter-label">Eveniment</span>
          <Select
            mode="multiple"
            allowClear
            placeholder="Toate"
            style={{ minWidth: 130 }}
            value={eventFilter}
            onChange={setEventFilter}
            options={eventOptions}
            maxTagCount="responsive"
          />
        </div>
        <div className="wm-filter" style={{ flex: "1 1 200px" }}>
          <span className="wm-filter-label">Căutare</span>
          <Input.Search
            placeholder="ExternalId, client, detalii..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 8,
          marginBottom: 12,
        }}
      >
        <p className="wm-results-note" style={{ margin: 0, flex: "1 1 auto" }}>
          {filteredLogs.length} request-uri găsite
          {outcomeFilter.length || eventFilter.length || search
            ? " (filtre active)"
            : ""}
        </p>
        {outcomeFilter.length || eventFilter.length || search ? (
          <button
            type="button"
            className="wm-btn wm-btn-ghost wm-btn-sm"
            onClick={resetFilters}
          >
            <ClearOutlined /> Resetează filtrele
          </button>
        ) : null}
        <button
          type="button"
          className="wm-btn wm-btn-secondary wm-btn-sm"
          onClick={exportCsv}
          disabled={!filteredLogs.length}
        >
          <DownloadOutlined /> Export CSV
        </button>
        <button
          type="button"
          className="wm-btn wm-btn-secondary wm-btn-sm"
          onClick={() => fetchAll()}
        >
          <ReloadOutlined /> Reîncarcă
        </button>
      </div>

      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={logColumns}
        dataSource={filteredLogs}
        pagination={paginationProps(filteredLogs.length, 20)}
        expandable={{ expandedRowRender: expandedRow }}
        locale={{ emptyText: "Niciun request în intervalul/filtrele selectate" }}
        scroll={{ x: 1000 }}
      />
    </>
  );

  const pendingTab = (
    <>
      <p className="wm-tab-desc">
        Rezervări create de clienți care încă NU au fost confirmate de Paynet.
        Dacă plata nu se confirmă în 2 ore, rezervarea se șterge automat
        (și apare în istoric ca „Rezervare expirată”).
      </p>
      <Table
        rowKey="id"
        size="small"
        loading={loading}
        columns={pendingColumns}
        dataSource={pending}
        pagination={{ ...paginationProps(pending.length, 10), hideOnSinglePage: true }}
        locale={{ emptyText: "Nicio rezervare în așteptare — totul e la zi" }}
      />
    </>
  );

  return (
    <div className="wm-page">
      {/* ===== SUMAR ===== */}
      <div className="wm-stats">
        <div className="wm-stat wm-stat-good">
          <div className="wm-stat-label">Plăți procesate</div>
          <div className="wm-stat-value">{countBy("confirmed")}</div>
          <div className="wm-stat-meta">{sumaProcesata.toFixed(2)} lei încasați</div>
        </div>
        <div className="wm-stat wm-stat-bad">
          <div className="wm-stat-label">Venite după expirare</div>
          <div className="wm-stat-value">{countBy("not_found")}</div>
          <div className="wm-stat-meta">webhook sosit prea târziu</div>
        </div>
        <div className="wm-stat wm-stat-warn">
          <div className="wm-stat-label">Rezervări expirate</div>
          <div className="wm-stat-value">{countBy("reservation_expired")}</div>
          <div className="wm-stat-meta">neachitate în 2 ore</div>
        </div>
        <div className="wm-stat">
          <div className="wm-stat-label">În așteptare acum</div>
          <div className="wm-stat-value">{pending.length}</div>
          <div className="wm-stat-meta">rezervări active</div>
        </div>
      </div>

      {/* ===== TABELE, PE TAB-URI ===== */}
      <div className="wm-card wm-history">
        <Tabs
          defaultActiveKey="istoric"
          items={[
            {
              key: "istoric",
              label: `Request-uri de la Paynet (${filteredLogs.length})`,
              children: historyTab,
            },
            {
              key: "pending",
              label: `Rezervări în așteptare (${pending.length})`,
              children: pendingTab,
            },
          ]}
        />
      </div>
    </div>
  );
};

export default PaynetRequests;
